<template>
  <transition name="ns-fade">
    <div v-if="cfg" class="next-step" role="region" aria-label="Próximo passo">
      <button type="button" class="ns-x" aria-label="Fechar" title="Fechar" @click="close('x')">✕</button>
      <p class="ns-eyebrow">Próximo passo</p>
      <h2>{{ cfg.title }}</h2>
      <p class="ns-text" v-safe-html="cfg.text"></p>
      <div class="ns-actions">
        <button v-if="cfg.primary" type="button" class="ns-primary" @click="run(cfg.primary, 'primary')">{{ cfg.primary.label }}</button>
        <button v-if="cfg.secondary" type="button" class="ns-secondary" @click="run(cfg.secondary, 'secondary')">{{ cfg.secondary.label }}</button>
      </div>
    </div>
  </transition>
</template>

<script>
import { track } from "@/utils/analytics";

// Right after the onboarding (?guide=<key>): one card on top of the page saying what to do there, with a button
// that does it. The element the guide talks about also gets a soft pulse.
const GUIDES = {
  ditar: {
    title: "Teste o ditado aqui",
    text: "Fale uns 30 segundos como se tivesse acabado de atender alguém. <b>Pode inventar</b>: a anotação fica neste paciente de exemplo, que dá para excluir depois.",
    primary: { label: "🎙️ Começar a falar", action: "trial" },
  },
  nota: {
    title: "Esta é a anotação que você ditou",
    text: "Ela fica no prontuário, com data e hora. Com um paciente de verdade é igual: clique em <b>Ditar atendimento</b>, no topo da tela, e diga o nome dele. Se ele ainda não tiver ficha, a IA cria.",
    primary: { label: "🎙️ Ditar um atendimento de verdade", action: "quicknote" },
    secondary: { label: "Ir para o início", route: { name: "Dashboard" } },
    pulse: ".btn-quick-note",
  },
  agenda: {
    title: "Comece marcando um atendimento",
    text: "Escolha o paciente, o dia e o horário. O atendimento aparece aqui e no financeiro, com lembrete para o paciente. Depois, se quiser, ative o agendamento online para os pacientes marcarem sozinhos.",
    primary: { label: "+ Marcar um atendimento", click: "+ Novo atendimento" },
    secondary: { label: "Ativar o agendamento online", route: { name: "Agenda", query: { setup: "booking" } } },
    pulseText: "+ Novo atendimento",
  },
  financeiro: {
    title: "O financeiro se monta com a agenda",
    text: "Cada atendimento marcado com valor aparece aqui: quem pagou, quem deve e o recibo em 1 clique. Comece registrando um atendimento.",
    primary: { label: "+ Registrar um atendimento", route: { name: "Agenda", query: { new: 1 } } },
    secondary: { label: "Cadastrar Pix e dados do recibo", click: "Pix e dados do recibo" },
    pulseText: "+ Atendimento",
  },
  paciente: {
    title: "Cadastre seu primeiro paciente",
    text: "Só o nome já basta; telefone e o resto você completa quando quiser. Ao salvar, já dá para anotar o primeiro atendimento dele.",
    secondary: { label: "🎙️ Prefiro ditar um atendimento (a IA cria a ficha)", action: "quicknote" },
  },
  migrar: {
    title: "Escolha como estão seus dados hoje",
    text: "Planilha, prontuário em arquivo, ou foto e lista de pacientes: escolha abaixo. Você confere tudo antes de importar.",
    secondary: { label: "Não tenho arquivo: cadastrar um paciente", route: { name: "CustomersAdd" } },
  },
};

export default {
  name: "ComponentNextStep",
  data() {
    return { pulsed: null };
  },
  computed: {
    key() {
      return this.$route.query.guide || "";
    },
    cfg() {
      const guide = GUIDES[this.key];
      if (!guide) return null;
      // "paciente" in the copy follows the account's word for it (Animal for vets).
      const word = (this.$store.getters["Companies/customerTreatmentSingular"] || "Paciente").toLowerCase();
      if (word === "paciente") return guide;
      const swap = (t) => t && t.replace(/paciente/g, word);
      return { ...guide, title: swap(guide.title), text: swap(guide.text) };
    },
  },
  watch: {
    key: {
      immediate: true,
      handler(key) {
        this.clearPulse();
        if (!GUIDES[key]) return;
        track("next_step_shown", { guide: key });
        setTimeout(() => this.addPulse(), 900); // after the page renders
      },
    },
  },
  mounted() {
    this.$eventbus.on("QuickNote/trialSaved", this.onTrialSaved);
  },
  beforeUnmount() {
    this.$eventbus.off("QuickNote/trialSaved", this.onTrialSaved);
    this.clearPulse();
  },
  methods: {
    findByText(text) {
      return [...document.querySelectorAll(".container-router-view button, .container-router-view a")].find(
        (el) => el.textContent.trim() === text && el.offsetParent !== null
      );
    },
    addPulse() {
      if (!this.cfg) return;
      const el = this.cfg.pulse ? document.querySelector(this.cfg.pulse) : this.cfg.pulseText && this.findByText(this.cfg.pulseText);
      if (!el) return;
      el.classList.add("guide-pulse");
      this.pulsed = el;
    },
    clearPulse() {
      if (this.pulsed) this.pulsed.classList.remove("guide-pulse");
      this.pulsed = null;
    },
    // Leaves the page as it is, without the card.
    dropGuide(extra = {}) {
      const query = { ...this.$route.query, ...extra };
      delete query.guide;
      return this.$router.replace({ query });
    },
    close(how) {
      track("next_step_close", { guide: this.key, how });
      this.dropGuide();
    },
    run(step, which) {
      track("next_step_click", { guide: this.key, which });
      if (step.action === "trial") {
        return this.$eventbus.emit("QuickNote/open", {
          source: "next_step", trial: true, customerId: Number(this.$route.params.id), customerName: "Paciente de exemplo", autostart: true,
        });
      }
      if (step.action === "quicknote") {
        this.dropGuide();
        return this.$eventbus.emit("QuickNote/open", { source: "next_step" });
      }
      if (step.route) {
        if (step.route.name === this.$route.name) return this.dropGuide(step.route.query || {});
        return this.$router.push(step.route);
      }
      if (step.click) {
        const el = this.findByText(step.click);
        this.dropGuide();
        if (el) el.click();
      }
    },
    onTrialSaved() {
      if (this.key !== "ditar") return;
      this.$router.replace({ query: { ...this.$route.query, guide: "nota" } });
    },
  },
};
</script>

<style scoped>
.next-step {
  flex: none;
  position: relative;
  margin: 16px 20px 4px;
  padding: 18px 48px 18px 20px;
  border: 1.5px solid #9fd9e6;
  border-left: 5px solid #29b1cc;
  border-radius: 14px;
  background: #f2fbfd;
  color: #1f2d33;
}
.ns-eyebrow { margin: 0 0 4px; color: #1a8ea8; font-size: 0.78em; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.next-step h2 { margin: 0 0 6px; font-size: 1.2em; font-weight: 700; color: #1f2d33; }
.ns-text { margin: 0 0 14px; max-width: 52em; color: #3d4f55; line-height: 1.55; }
.ns-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.ns-primary, .ns-secondary { min-height: 42px; padding: 0 18px; border-radius: 10px; font: inherit; font-weight: 700; cursor: pointer; }
.ns-primary { border: 0; background: #29b1cc; color: #fff; box-shadow: 0 4px 12px rgba(41, 177, 204, 0.25); }
.ns-primary:hover { background: #1a8ea8; }
.ns-secondary { border: 1.5px solid #29b1cc; background: #fff; color: #1a8ea8; }
.ns-secondary:hover { background: #e9f7fa; }
.ns-x { position: absolute; top: 10px; right: 10px; width: 34px; height: 34px; border: 0; border-radius: 8px; background: transparent; color: #6b7d82; font-size: 1em; cursor: pointer; }
.ns-x:hover { background: rgba(0, 0, 0, 0.05); }
.ns-fade-enter-active, .ns-fade-leave-active { transition: opacity 0.2s; }
.ns-fade-enter-from, .ns-fade-leave-to { opacity: 0; }
@media (max-width: 640px) {
  /* Compact on phones: the page below must stay visible. */
  .next-step { margin: 8px 10px 2px; padding: 12px 40px 12px 14px; border-left-width: 4px; border-radius: 12px; }
  .ns-eyebrow { display: none; }
  .next-step h2 { font-size: 1.02em; margin-bottom: 4px; }
  .ns-text { font-size: 0.9em; line-height: 1.45; margin-bottom: 10px; }
  .ns-actions { gap: 8px; }
  .ns-primary, .ns-secondary { min-height: 38px; padding: 0 12px; font-size: 0.92em; }
  .ns-x { top: 6px; right: 6px; }
}
</style>

<style>
/* The element a "next step" guide talks about (global: it lives in another component). */
.guide-pulse { animation: guide-pulse 1.6s ease-out 4; border-radius: 10px; }
@keyframes guide-pulse {
  0% { box-shadow: 0 0 0 0 rgba(41, 177, 204, 0.65); }
  100% { box-shadow: 0 0 0 14px rgba(41, 177, 204, 0); }
}
@media (prefers-reduced-motion: reduce) { .guide-pulse { animation: none; outline: 3px solid #29b1cc; } }
</style>
